import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NewsletterStatusBadge from "./NewsletterStatusBadge";
import SendConfirmModal from "./SendConfirmModal";
import styles from "./Newsletters.module.css";
import { formatNewsletterDate, getLatestAudienceSync } from "./newsletterUtils";

const topicTypeOptions = [
  { value: "hot_topic", label: "Hot topic (news-driven)" },
  { value: "feature_spotlight", label: "Feature Spotlight" },
  { value: "new_product", label: "New Product" },
  { value: "other", label: "Other" },
];

const featureTopicOptions = [
  { value: "ease_analytics", label: "Ease Analytics" },
  { value: "ease_ai", label: "Ease AI" },
  { value: "ease_marketplace", label: "Ease MarketPlace" },
  { value: "my_apps", label: "My Apps" },
  { value: "tracking", label: "Tracking" },
  { value: "billing", label: "Billing" },
  { value: "auto_discovery", label: "Auto Discovery" },
  { value: "keep_expense", label: "Expense Tracker / KeepExpense" },
  { value: "auto_payments", label: "Auto Payments" },
  { value: "ease_demo", label: "Ease Demo" },
  { value: "ease_settings", label: "Ease Settings" },
  { value: "workspace_management", label: "Workspace Management" },
  { value: "team_invites", label: "Team Invites and Access Control" },
  {
    value: "subscription_management",
    label: "Subscription and Renewal Management",
  },
  {
    value: "checkout_flow",
    label: "Marketplace Checkout and Purchase Flow",
  },
  { value: "domains", label: "Domain Purchase and Management" },
];

const emptyGenerateForm = {
  month: "",
  topic_type: "feature_spotlight",
  topic_key: "",
  note_to_ai: "",
};

function NewsletterListPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    month: "",
    topic_type: "",
    search: "",
  });
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState(emptyGenerateForm);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [audienceSummary, setAudienceSummary] = useState({
    total_lists: 0,
    last_synced_audience_at: "",
  });
  const [syncResult, setSyncResult] = useState({
    synced: "-",
    failed: "-",
    updated_drafts: "-",
  });
  const [latestSyncSummary, setLatestSyncSummary] = useState(null);
  const [sendDialogState, setSendDialogState] = useState({
    open: false,
    draft: null,
  });
  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false,
    draft: null,
  });

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) query.set(key, value);
      });
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters${
          query.toString() ? `?${query.toString()}` : ""
        }`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to load newsletters");
      }
      const nextDrafts = Array.isArray(data.data) ? data.data : [];
      setDrafts(nextDrafts);
      const latestSyncDraft = getLatestAudienceSync(nextDrafts);
      const apiAudienceSummary = data?.audience_summary || {};
      const summaryFromRows = latestSyncDraft
        ? {
            total_lists: 0,
            last_synced_audience_at:
              latestSyncDraft?.last_synced_audience_at || "",
          }
        : null;
      const nextSummary = {
        total_lists:
          apiAudienceSummary?.total_lists ??
          latestSyncSummary?.total_lists ??
          summaryFromRows?.total_lists ??
          0,
        last_synced_audience_at:
          apiAudienceSummary?.last_synced_audience_at ||
          latestSyncSummary?.last_synced_audience_at ||
          summaryFromRows?.last_synced_audience_at ||
          "",
      };
      setAudienceSummary({
        total_lists: nextSummary.total_lists,
        last_synced_audience_at: nextSummary.last_synced_audience_at,
      });
      if (apiAudienceSummary?.total_lists != null) {
        setLatestSyncSummary(nextSummary);
      }
      setSyncResult((prev) => ({
        synced: apiAudienceSummary?.synced ?? prev.synced ?? "-",
        failed: apiAudienceSummary?.failed ?? prev.failed ?? "-",
        updated_drafts:
          apiAudienceSummary?.updated_drafts ?? prev.updated_drafts ?? "-",
      }));
    } catch (error) {
      toast.error(error.message || "Unable to load newsletters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
    // eslint-disable-next-line
  }, [filters.status, filters.month, filters.topic_type, filters.search]);

  const topicTypes = useMemo(
    () =>
      Array.from(new Set(drafts.map((item) => item.topic_type).filter(Boolean))),
    [drafts],
  );

  const months = useMemo(
    () => Array.from(new Set(drafts.map((item) => item.month).filter(Boolean))),
    [drafts],
  );

  const topicKeyOptions = useMemo(() => {
    if (generateForm.topic_type === "new_product") {
      return productOptions;
    }
    return featureTopicOptions;
  }, [generateForm.topic_type, productOptions]);

  const fetchProductOptions = async () => {
    setProductsLoading(true);
    setProductsError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/marketplace/get`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Unable to load products");
      }
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      setProductOptions(
        items.map((item) => ({
          value: String(item.id),
          label: item.name || item.marketplaceName || `Product ${item.id}`,
        })),
      );
    } catch (error) {
      setProductOptions([]);
      setProductsError(error.message || "Unable to load products");
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (!generateOpen) return;
    if (generateForm.topic_type !== "new_product") return;
    if (productOptions.length || productsLoading) return;
    fetchProductOptions();
    // eslint-disable-next-line
  }, [generateOpen, generateForm.topic_type]);

  const handleSyncAudience = async () => {
    setActionLoading("sync-audience");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/sync-audience`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to sync audience");
      }
      const failedCount = Number(data?.data?.failed || 0);
      setAudienceSummary({
        total_lists: data?.data?.total_lists || 0,
        last_synced_audience_at:
          data?.data?.last_synced_audience_at || new Date().toISOString(),
      });
      setLatestSyncSummary({
        total_lists: data?.data?.total_lists || 0,
        last_synced_audience_at:
          data?.data?.last_synced_audience_at || new Date().toISOString(),
      });
      setSyncResult({
        synced: data?.data?.synced ?? 0,
        failed: data?.data?.failed ?? 0,
        updated_drafts: data?.data?.updated_drafts ?? "-",
      });
      if (failedCount > 0) {
        toast.warn(
          `Audience sync completed with issues. Total lists: ${data?.data?.total_lists || 0}, synced: ${data?.data?.synced || 0}, failed: ${failedCount}`,
        );
      } else {
        toast.success(
          `Audience sync completed. Total lists: ${data?.data?.total_lists || 0}, synced: ${data?.data?.synced || 0}, failed: 0`,
        );
      }
      fetchDrafts();
    } catch (error) {
      toast.error(error.message || "Unable to sync audience");
    } finally {
      setActionLoading("");
    }
  };

  const callRowAction = async (id, action, extraPayload = {}) => {
    const key = `${action}-${id}`;
    setActionLoading(key);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}/${action}`,
        {
          method: "POST",
          headers:
            action === "send"
              ? {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                }
              : undefined,
          credentials: "include",
          body:
            action === "send"
              ? JSON.stringify({
                  campaign_list_key: extraPayload?.campaign_list_key || null,
                  ...extraPayload,
                })
              : undefined,
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || `Unable to ${action} newsletter`);
      }
      toast.success(data?.message || `Newsletter ${action}d successfully`);
      if (action === "send") {
        setSendDialogState({ open: false, draft: null });
      }
      fetchDrafts();
    } catch (error) {
      toast.error(error.message || `Unable to ${action} newsletter`);
    } finally {
      setActionLoading("");
    }
  };

  const handleDeleteDraft = async () => {
    const id = deleteDialogState?.draft?.id;
    if (!id) return;
    setActionLoading(`delete-${id}`);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to delete newsletter");
      }
      toast.success(data?.message || "Newsletter deleted");
      setDeleteDialogState({ open: false, draft: null });
      fetchDrafts();
    } catch (error) {
      toast.error(error.message || "Unable to delete newsletter");
    } finally {
      setActionLoading("");
    }
  };

  const handleGenerate = async () => {
    setActionLoading("generate");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/generate`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(generateForm),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to generate draft");
      }
      setGenerateOpen(false);
      setGenerateForm(emptyGenerateForm);
      toast.success("Draft generated successfully");
      fetchDrafts();
      if (data?.data?.id) navigate(`/dashboard/newsletters/${data.data.id}`);
    } catch (error) {
      toast.error(error.message || "Unable to generate draft");
    } finally {
      setActionLoading("");
    }
  };

  const handleTopicTypeChange = (value) => {
    setGenerateForm((prev) => ({
      ...prev,
      topic_type: value,
      topic_key: "",
      note_to_ai: "",
    }));
    if (value === "new_product" && !productOptions.length && !productsLoading) {
      fetchProductOptions();
    }
  };

  const getVisibleActions = (draft) => {
    const normalizedStatus = String(draft?.status || "").toLowerCase();
    if (normalizedStatus === "approved") {
      return {
        showApprove: false,
        showUnapprove: true,
        showSend: true,
        showDelete: true,
      };
    }
    if (normalizedStatus === "sent") {
      return {
        showApprove: false,
        showUnapprove: false,
        showSend: false,
        showDelete: false,
      };
    }
    return {
      showApprove: true,
      showUnapprove: false,
      showSend: false,
      showDelete: true,
    };
  };

  return (
    <div className={styles.pageShell}>
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.eyebrow}>Manual Approval Workflow</div>
          <h1 className={styles.pageTitle}>Newsletter Drafts</h1>
          <p className={styles.pageSubtitle}>
            Review AI-generated campaigns, refine the copy, approve, and send when ready.
          </p>
        </div>
        <div className={styles.pageHeaderActions}>
          <Button
            variant="outlined"
            onClick={handleSyncAudience}
            disabled={actionLoading === "sync-audience"}
            className={styles.secondaryAction}
          >
            {actionLoading === "sync-audience" ? "Syncing..." : "Sync Audience"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDrafts}
            className={styles.secondaryAction}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setGenerateOpen(true)}
            className={styles.primaryAction}
          >
            Generate Draft
          </Button>
        </div>
      </div>

      <div className={styles.syncSummaryGrid}>
        <Paper className={styles.summaryCard}>
          <span>Total Lists</span>
          <strong>{audienceSummary.total_lists || 0}</strong>
        </Paper>
        <Paper className={styles.summaryCard}>
          <span>Last Sync</span>
          <strong>{formatNewsletterDate(audienceSummary.last_synced_audience_at)}</strong>
        </Paper>
        <Paper className={styles.summaryCard}>
          <span>Synced</span>
          <strong>{syncResult.synced}</strong>
        </Paper>
        <Paper
          className={`${styles.summaryCard} ${
            Number(syncResult.failed || 0) > 0 ? styles.summaryCardWarning : ""
          }`}
        >
          <span>Failed</span>
          <strong>{syncResult.failed}</strong>
        </Paper>
      </div>

      <div className={styles.filtersBar}>
        <Select
          displayEmpty
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, status: e.target.value }))
          }
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </Select>

        <Select
          displayEmpty
          value={filters.month}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, month: e.target.value }))
          }
        >
          <MenuItem value="">All Months</MenuItem>
          {months.map((month) => (
            <MenuItem value={month} key={month}>
              {month}
            </MenuItem>
          ))}
        </Select>

        <Select
          displayEmpty
          value={filters.topic_type}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, topic_type: e.target.value }))
          }
        >
          <MenuItem value="">All Topic Types</MenuItem>
          {topicTypes.map((type) => (
            <MenuItem value={type} key={type}>
              {type}
            </MenuItem>
          ))}
        </Select>

        <TextField
          fullWidth
          placeholder="Search by subject or topic"
          value={filters.search}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, search: e.target.value }))
          }
        />
      </div>

      <Paper className={styles.tableCard}>
        {loading ? (
          <div className={styles.loadingState}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer>
            <Table className={styles.newsTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Draft ID</TableCell>
                  <TableCell>Month</TableCell>
                  <TableCell>Topic Type</TableCell>
                  <TableCell>Topic Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Last Sync</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drafts.length ? (
                  drafts.map((draft) => {
                    const actions = getVisibleActions(draft);

                    return (
                      <TableRow
                        key={draft.id}
                        hover
                        className={styles.clickableRow}
                        onClick={() => navigate(`/dashboard/newsletters/${draft.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/dashboard/newsletters/${draft.id}`);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                      >
                      <TableCell>#{draft.id}</TableCell>
                      <TableCell>{draft.month}</TableCell>
                      <TableCell>{draft.topic_type}</TableCell>
                      <TableCell>{draft.topic_title}</TableCell>
                      <TableCell>
                        <NewsletterStatusBadge status={draft.status} />
                      </TableCell>
                      <TableCell>{draft.audience_count}</TableCell>
                      <TableCell>
                        {formatNewsletterDate(draft.last_synced_audience_at)}
                      </TableCell>
                      <TableCell className={styles.subjectCell}>
                        {draft.subject}
                      </TableCell>
                      <TableCell>{formatNewsletterDate(draft.createdAt)}</TableCell>
                      <TableCell>{formatNewsletterDate(draft.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <div className={styles.rowActions}>
                          <Button
                            size="small"
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/newsletters/${draft.id}`);
                            }}
                          >
                            Open
                          </Button>
                          {actions.showApprove ? (
                            <Button
                              size="small"
                              disabled={actionLoading === `approve-${draft.id}`}
                              startIcon={<CheckCircleOutlineIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                callRowAction(draft.id, "approve");
                              }}
                            >
                              Approve
                            </Button>
                          ) : null}
                          {actions.showUnapprove ? (
                            <Button
                              size="small"
                              disabled={actionLoading === `unapprove-${draft.id}`}
                              startIcon={<EditOutlinedIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                callRowAction(draft.id, "unapprove");
                              }}
                            >
                              Unapprove
                            </Button>
                          ) : null}
                          {actions.showSend ? (
                            <Button
                              size="small"
                              disabled={actionLoading === `send-${draft.id}`}
                              startIcon={<SendOutlinedIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSendDialogState({ open: true, draft });
                              }}
                            >
                              Send
                            </Button>
                          ) : null}
                          {actions.showDelete ? (
                            <Button
                              size="small"
                              color="error"
                              disabled={actionLoading === `delete-${draft.id}`}
                              startIcon={<DeleteOutlineIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteDialogState({ open: true, draft });
                              }}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <div className={styles.emptyState}>
                        <h3>No newsletters found</h3>
                        <p>Try adjusting the filters or generate a new draft.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Generate Newsletter Draft</DialogTitle>
        <DialogContent className={styles.generateDialogBody}>
          <TextField
            label="Month"
            type="month"
            value={generateForm.month}
            onChange={(e) =>
              setGenerateForm((prev) => ({ ...prev, month: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth>
            <InputLabel id="newsletter-topic-type-label">Topic Type</InputLabel>
            <Select
              labelId="newsletter-topic-type-label"
              label="Topic Type"
              value={generateForm.topic_type}
              onChange={(e) => handleTopicTypeChange(e.target.value)}
            >
              {topicTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {generateForm.topic_type !== "other" &&
          generateForm.topic_type !== "hot_topic" ? (
            <FormControl
              fullWidth
              disabled={
                generateForm.topic_type === "new_product" &&
                (productsLoading || Boolean(productsError))
              }
            >
              <InputLabel id="newsletter-topic-key-label">
                {generateForm.topic_type === "new_product" ? "Product" : "Feature"}
              </InputLabel>
              <Select
                labelId="newsletter-topic-key-label"
                label={generateForm.topic_type === "new_product" ? "Product" : "Feature"}
                value={generateForm.topic_key}
                onChange={(e) =>
                  setGenerateForm((prev) => ({ ...prev, topic_key: e.target.value }))
                }
              >
                {topicKeyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <TextField
            label="Note to AI (Optional)"
            multiline
            minRows={3}
            value={generateForm.note_to_ai}
            onChange={(e) =>
              setGenerateForm((prev) => ({ ...prev, note_to_ai: e.target.value }))
            }
            helperText={
              generateForm.topic_type === "other"
                ? "Use this note to describe what kind of newsletter you want."
                : "Optional extra context to guide AI draft generation."
            }
          />
          {generateForm.topic_type === "new_product" && productsLoading ? (
            <div className={styles.inlineLoader}>
              <CircularProgress size={18} />
              <span>Loading products...</span>
            </div>
          ) : null}
          {generateForm.topic_type === "new_product" && productsError ? (
            <Alert severity="error">{productsError}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={actionLoading === "generate"}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={
              actionLoading === "generate" ||
              !generateForm.month ||
              !generateForm.topic_type ||
              (generateForm.topic_type !== "other" &&
                generateForm.topic_type !== "hot_topic" &&
                !generateForm.topic_key)
            }
          >
            {actionLoading === "generate" ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogState.open}
        onClose={() => {
          if (actionLoading === `delete-${deleteDialogState?.draft?.id}`) return;
          setDeleteDialogState({ open: false, draft: null });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete newsletter draft?</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0 }}>
            This will permanently remove
            {deleteDialogState?.draft?.subject
              ? ` "${deleteDialogState.draft.subject}"`
              : ` draft #${deleteDialogState?.draft?.id || ""}`}
            . This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogState({ open: false, draft: null })}
            disabled={actionLoading === `delete-${deleteDialogState?.draft?.id}`}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteDraft}
            disabled={actionLoading === `delete-${deleteDialogState?.draft?.id}`}
          >
            {actionLoading === `delete-${deleteDialogState?.draft?.id}`
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <SendConfirmModal
        open={sendDialogState.open}
        onClose={() => setSendDialogState({ open: false, draft: null })}
        onConfirm={(payload) => {
          if (!sendDialogState?.draft?.id) return;
          callRowAction(sendDialogState.draft.id, "send", {
            campaign_list_key: sendDialogState?.draft?.campaign_list_key || null,
            ...payload,
          });
        }}
        loading={
          Boolean(sendDialogState?.draft?.id) &&
          actionLoading === `send-${sendDialogState.draft.id}`
        }
        audienceCount={sendDialogState?.draft?.audience_count || 0}
      />
    </div>
  );
}

export default NewsletterListPage;
